/**
 * Diagnostic tag badges for a question answer option (admin read-only).
 *
 * @module OptionTagBadges
 */

import {
  optionTagConfidence,
  optionTags,
  type OptionValue,
} from '@/lib/questionOptions';

interface OptionTagBadgesProps {
  readonly option: OptionValue;
}

function confidenceClass(confidence: string): string {
  if (confidence === 'high') {
    return 'bg-emerald-100 text-emerald-700';
  }
  if (confidence === 'low') {
    return 'bg-amber-100 text-amber-700';
  }
  return 'bg-slate-100 text-slate-600';
}

/**
 * Renders diagnostic tags (and optional confidence) for one answer option.
 */
export default function OptionTagBadges({ option }: OptionTagBadgesProps) {
  const tags = optionTags(option);
  if (tags.length === 0) {
    return null;
  }

  const confidence = optionTagConfidence(option);

  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {tags.map((tag) => (
        <span
          key={tag}
          className="inline-flex items-center gap-1 rounded border border-violet-200 bg-violet-50 px-1.5 py-0.5 font-mono text-[10px] text-violet-800"
          title="Diagnostic tag"
        >
          {tag}
          {confidence ? (
            <span
              className={`rounded px-1 font-ui text-[9px] ${confidenceClass(confidence)}`}
            >
              {confidence}
            </span>
          ) : null}
        </span>
      ))}
    </span>
  );
}
